'use client';

import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import { analytics } from '@/lib/analytics';

interface HeroSectionProps {
  className?: string;
}

const HeroSection = ({ className = '' }: HeroSectionProps) => {
  return (
    <section className={`relative bg-gradient-to-br from-blue-50 to-white overflow-hidden ${className}`}>
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, rgb(37, 99, 235) 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="container mx-auto px-4 py-20 lg:py-32 relative z-10">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          
          {/* Main Headline */}
          <h1 className="font-serif text-5xl lg:text-6xl leading-tight text-blue-600 font-bold mx-auto">
            Professional Exterior Cleaning Services
          </h1>
          
          {/* Subheadline */}
          <p className="text-xl lg:text-2xl text-gray-700 leading-relaxed mx-auto">
            What could take you weeks of weekends and hard work, we take care of in just one visit. With professional tools and a sharp eye for detail, Crystal Clean makes your home look its best — quickly, easily, and stress-free.
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4 mx-auto">
            <Link
              href="/get-quote"
              className="inline-flex items-center justify-center px-8 py-4 bg-green-600 text-white font-semibold rounded-full hover:bg-green-700 transition-smooth shadow-brand text-lg btn-bubbly"
              onClick={() => analytics.trackCTAClick('Get Free Quote', 'Homepage Hero')}
            >
              Get Your Free Quote
              <Icon name="ArrowRightIcon" size={24} className="ml-2" />
            </Link>
            <a
              href="tel:6046718010"
              className="inline-flex items-center justify-center px-8 py-4 bg-blue-600 text-white font-bold text-lg rounded-lg hover:bg-blue-700 transition-smooth shadow-brand btn-bubbly"
            >
              <Icon name="PhoneIcon" size={20} className="mr-2" />
              Call (236) 867-2004
            </a>
          </div>
          
          {/* Trust Indicators */}
          <div className="flex flex-wrap items-center justify-center gap-6 pt-8 mx-auto">
            <div className="flex items-center space-x-2">
              <Icon name="StarIcon" size={24} className="text-yellow-500" variant="solid" />
              <span className="text-lg font-semibold text-gray-700">4.9/5 Rating</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="CheckBadgeIcon" size={24} className="text-green-600" variant="solid" />
              <span className="text-lg font-semibold text-gray-700">5+ Years Experience</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="ShieldCheckIcon" size={24} className="text-blue-600" variant="solid" />
              <span className="text-lg font-semibold text-gray-700">Fully Insured</span>
            </div>
          </div>

          {/* Featured Image */}
          <div className="mt-12 rounded-2xl overflow-hidden shadow-2xl mx-auto max-w-3xl">
            <AppImage
              src="/assets/images/ChatGPT_Image_Jan_30_2026_03_53_31_PM-1769784819275.png"
              alt="Professional exterior cleaning services"
              className="w-full h-[400px] object-cover" />
          </div>
        </div>
      </div>
    </section>);

};

export default HeroSection;